
import { useState } from 'react'
import { Plus, Copy, Trash2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { MealBuilder } from './meal-builder'
import { AIDietPlanGenerator } from '@/components/diet-plans-v2/ai-diet-plan-generator'
import { useGenerateDietPlanV2WithAI } from '@/api/diet-plans-v2'
import { useToast } from '@/hooks/use-toast'
import type { DietDay, Meal, MealType, DurationType, GoalType } from '@/types/diet-v2'

interface DietPlanBuilderV2Props {
  onSave: (data: {
    name: string
    type: GoalType
    duration: DurationType
    description: string
    days: Omit<DietDay, 'totalCalories' | 'macros'>[]
    isTemplate: boolean
  }) => void
  initialData?: {
    name: string
    type: GoalType
    duration: DurationType
    description: string
    days: DietDay[]
    isTemplate: boolean
  }
}

export function DietPlanBuilderV2({ onSave, initialData }: DietPlanBuilderV2Props) {
  const { toast } = useToast()
  const [name, setName] = useState(initialData?.name || '')
  const [type, setType] = useState<GoalType>(initialData?.type || 'weight-loss')
  const [duration, setDuration] = useState<DurationType>(initialData?.duration || 'week')
  const [description, setDescription] = useState(initialData?.description || '')
  const [isTemplate, setIsTemplate] = useState(initialData?.isTemplate ?? true)
  const [showAIDialog, setShowAIDialog] = useState(false)
  
  const generateAI = useGenerateDietPlanV2WithAI()

  // Initialize days based on duration
  const getDaysCount = () => {
    if (duration === 'day') return 1
    if (duration === 'week') return 7
    return 30 // month
  }

  const initializeDay = (dayNumber: number): Omit<DietDay, 'totalCalories' | 'macros'> => ({
    dayNumber,
    meals: [],
  })

  const [days, setDays] = useState<Omit<DietDay, 'totalCalories' | 'macros'>[]>(
    initialData?.days || Array.from({ length: getDaysCount() }, (_, i) => initializeDay(i + 1))
  )

  // Update days when duration changes
  const handleDurationChange = (newDuration: DurationType) => {
    setDuration(newDuration)
    const newCount = newDuration === 'day' ? 1 : newDuration === 'week' ? 7 : 30
    if (newCount > days.length) {
      setDays([...days, ...Array.from({ length: newCount - days.length }, (_, i) => initializeDay(days.length + i + 1))])
    } else {
      setDays(days.slice(0, newCount))
    }
  }

  const addMealToDay = (dayIndex: number, mealType: MealType) => {
    const newMeal: Omit<Meal, '_id' | 'totalCalories' | 'macros'> = {
      type: mealType,
      time: '',
      foods: [],
    }

    setDays((prev) =>
      prev.map((day, i) => (i === dayIndex ? { ...day, meals: [...day.meals, newMeal] } : day)) as Omit<DietDay, 'totalCalories' | 'macros'>[]
    )
  }

  const updateMeal = (dayIndex: number, mealIndex: number, meal: Omit<Meal, '_id' | 'totalCalories' | 'macros'>) => {
    setDays((prev) =>
      prev.map((day, i) =>
        i === dayIndex
          ? {
              ...day,
              meals: day.meals.map((m, mi) => (mi === mealIndex ? meal : m)),
            }
          : day
      ) as Omit<DietDay, 'totalCalories' | 'macros'>[]
    )
  }

  const removeMeal = (dayIndex: number, mealIndex: number) => {
    setDays((prev) =>
      prev.map((day, i) =>
        i === dayIndex
          ? {
              ...day,
              meals: day.meals.filter((_, mi) => mi !== mealIndex),
            }
          : day
      ) as Omit<DietDay, 'totalCalories' | 'macros'>[]
    )
  }

  const copyDay = (sourceDayIndex: number, targetDayIndex: number) => {
    const sourceDay = days[sourceDayIndex]
    setDays((prev) =>
      prev.map((day, i) =>
        i === targetDayIndex
          ? {
              dayNumber: day.dayNumber,
              meals: JSON.parse(JSON.stringify(sourceDay.meals)), // Deep copy
            }
          : day
      ) as Omit<DietDay, 'totalCalories' | 'macros'>[]
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      name,
      type,
      duration,
      description,
      days,
      isTemplate,
    })
  }

  const handleAIGenerate = async (prompt: string) => {
    try {
      const result = await generateAI.mutateAsync({ prompt })
      
      // Populate form with AI generated data
      if (result) {
        setName(result.name || name)
        setType((result.type && result.type !== 'custom' ? result.type : type) as GoalType)
        setDuration(result.duration || duration)
        setDescription(result.description || description)
        
        // Convert AI generated days to our format; normalize foods (source, fiber, fdcId)
        if (result.days && result.days.length > 0) {
          setDays(result.days.map((day) => ({
            dayNumber: day.dayNumber,
            date: day.date,
            meals: (day.meals || []).map((meal: { type: string; time?: string; notes?: string; foods?: unknown[] }) => ({
              type: meal.type,
              time: meal.time || '',
              notes: meal.notes || '',
              foods: ((meal.foods || []) as { name: string; source?: string; fdcId?: string; servingSize: number; servingUnit: string; calories: number; macros?: { protein: number; carbs: number; fat: number; fiber?: number }; customData?: unknown }[]).map((food) => {
                const source = (food.source === 'usda' || food.source === 'custom' || food.source === 'ai') ? food.source : 'ai'
                const foodItem: { name: string; source: 'usda' | 'custom' | 'ai'; servingSize: number; servingUnit: string; calories: number; macros: { protein: number; carbs: number; fat: number; fiber: number }; fdcId?: string; customData?: unknown } = {
                  name: food.name,
                  source,
                  servingSize: food.servingSize,
                  servingUnit: food.servingUnit,
                  calories: food.calories,
                  macros: {
                    protein: food.macros?.protein ?? 0,
                    carbs: food.macros?.carbs ?? 0,
                    fat: food.macros?.fat ?? 0,
                    fiber: food.macros?.fiber ?? 0,
                  },
                }
                if (source === 'usda' && food.fdcId) foodItem.fdcId = food.fdcId
                if (food.customData) foodItem.customData = food.customData
                return foodItem
              }),
            })),
          })) as Omit<DietDay, 'totalCalories' | 'macros'>[])
        }
        
        toast({
          title: 'Success',
          description: 'AI generated diet plan loaded. Review and edit as needed.',
        })
        
        setShowAIDialog(false)
      }
    } catch (error) {
      console.error('AI generation error:', error)
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* AI Generation Button */}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowAIDialog(true)}
            className="gap-2"
          >
            <Sparkles className="h-4 w-4" />
            Generate with AI
          </Button>
        </div>

        {/* Plan Details */}
        <Card className="p-6 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="planName">Plan Name *</Label>
          <Input
            id="planName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Weight Loss - 7 Day Plan"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="planType">Plan Type *</Label>
            <Select value={type} onValueChange={(v: GoalType) => setType(v)}>
              <SelectTrigger id="planType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weight-loss">Weight Loss</SelectItem>
                <SelectItem value="muscle-gain">Muscle Gain</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">Duration *</Label>
            <Select value={duration} onValueChange={(v: DurationType) => handleDurationChange(v)}>
              <SelectTrigger id="duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Single Day</SelectItem>
                <SelectItem value="week">Week (7 days)</SelectItem>
                <SelectItem value="month">Month (30 days)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this diet plan..."
            rows={3}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Switch id="isTemplate" checked={isTemplate} onCheckedChange={setIsTemplate} />
          <Label htmlFor="isTemplate">Save as template (reusable for multiple users)</Label>
        </div>
      </Card>

      {/* Day-by-Day Builder */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Daily Meal Plans</h3>
        <Tabs defaultValue="day-1">
          <TabsList className="flex-wrap h-auto">
            {days.map((day, index) => (
              <TabsTrigger key={day.dayNumber} value={`day-${day.dayNumber}`}>
                Day {day.dayNumber}
              </TabsTrigger>
            ))}
          </TabsList>

          {days.map((day, dayIndex) => (
            <TabsContent key={day.dayNumber} value={`day-${day.dayNumber}`} className="space-y-4">
              {/* Copy Day Actions */}
              {days.length > 1 && (
                <Card className="p-4">
                  <div className="flex items-center gap-2">
                    <Label>Copy meals from Day {day.dayNumber} to:</Label>
                    <Select onValueChange={(v) => copyDay(dayIndex, parseInt(v))}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select day" />
                      </SelectTrigger>
                      <SelectContent>
                        {days
                          .filter((_, i) => i !== dayIndex)
                          .map((d, i) => (
                            <SelectItem key={d.dayNumber} value={days.findIndex((day) => day.dayNumber === d.dayNumber).toString()}>
                              Day {d.dayNumber}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </Card>
              )}

              {/* Meals */}
              <div className="space-y-4">
                {day.meals.map((meal, mealIndex) => (
                  <MealBuilder
                    key={mealIndex}
                    meal={meal}
                    onChange={(updatedMeal) => updateMeal(dayIndex, mealIndex, updatedMeal)}
                    onRemove={() => removeMeal(dayIndex, mealIndex)}
                  />
                ))}
              </div>

              {/* Add Meal Buttons */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addMealToDay(dayIndex, 'breakfast')}
                  disabled={day.meals.some((m) => m.type === 'breakfast')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Breakfast
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addMealToDay(dayIndex, 'lunch')}
                  disabled={day.meals.some((m) => m.type === 'lunch')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Lunch
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addMealToDay(dayIndex, 'dinner')}
                  disabled={day.meals.some((m) => m.type === 'dinner')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Dinner
                </Button>
                <Button type="button" variant="outline" onClick={() => addMealToDay(dayIndex, 'snack')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Snack
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

        {/* Submit */}
        <div className="flex justify-end gap-4 sticky bottom-0 bg-background py-4 border-t">
          <Button type="submit" size="lg">
            Save Diet Plan
          </Button>
        </div>
      </form>

      {/* AI Generation Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generate Diet Plan with AI</DialogTitle>
          </DialogHeader>
          <AIDietPlanGenerator
            title="Describe Your Diet Plan"
            description="Tell us what kind of diet plan you want to create. Be specific about goals, dietary restrictions, and preferences."
            placeholder="Example: Create a 7-day weight loss plan for a vegetarian diet, 1800 calories per day, with high protein meals. Include breakfast, lunch, dinner and one snack. Avoid dairy products."
            onGenerate={handleAIGenerate}
            isGenerating={generateAI.isPending}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
